import orderUtils from 'utils/Order';
import checkoutUtils from 'utils/Checkout';
import skuHelpers from 'utils/skuHelpers';
import { PAYMENT_TYPES } from 'constants/RwdBasket';
import { ALTERNATE_PAYMENT_TYPES } from 'constants/frictionlessCheckout';
import Venmo from 'utils/Venmo';
import localeUtils from 'utils/LanguageLocale';

function paymentMethodCondition(isNewUserFlow) {
    return !isNewUserFlow || checkoutUtils.isGuestOrder();
}

function getAvailablePaymentMethods(orderDetails, isNewUserFlow, klarnaError) {
    const isKlarnaEnabledForThisOrder = orderUtils.isKlarnaEnabledForThisOrder(orderDetails) && !klarnaError && paymentMethodCondition(isNewUserFlow);
    const isAfterpayEnabledForThisOrder = orderUtils.isAfterpayEnabledForThisOrder(orderDetails) && paymentMethodCondition(isNewUserFlow);
    const isPazeEnabledForThisOrder = orderUtils.isPazeEnabledForThisOrder(orderDetails) && paymentMethodCondition(isNewUserFlow);
    const isVenmoEnabledForThisOrder = orderUtils.isVenmoEnabledForThisOrder(orderDetails) && paymentMethodCondition(isNewUserFlow);
    const isAfterpayEnabledForThisProfile = checkoutUtils.isAfterpayEnabledForThisProfile(orderDetails);
    const isPayPalEnabled = orderUtils.isPayPalEnabled(orderDetails);
    const isPayPalPayLaterEligible = Sephora.configurationSettings.isPayPalEnabled && orderDetails?.items?.isPayPalPayLaterEligible;
    const isGooglePayEnabledForThisOrder = orderUtils.isGooglePayEnabledForThisOrder(orderDetails) && paymentMethodCondition(isNewUserFlow);

    return {
        isAfterpayEnabledForThisOrder,
        isAfterpayEnabledForThisProfile,
        isPayPalEnabled,
        isPayPalPayLaterEligible,
        isKlarnaEnabledForThisOrder,
        isPazeEnabledForThisOrder,
        isVenmoEnabledForThisOrder,
        isGooglePayEnabledForThisOrder
    };
}

function getGiftCardPaymentGroup(paymentGroups) {
    return (paymentGroups?.paymentGroupsEntries || []).filter(group => group.paymentGroupType === orderUtils.PAYMENT_GROUP_TYPE.GIFT_CARD);
}

function checkIfAlternateMethodSelected(afterpay, klarna, paze, venmo, googlePay = { isSelected: false }) {
    return afterpay.isSelected || klarna.isSelected || paze.isSelected || venmo.isSelected || googlePay.isSelected;
}

function getAlternatePaymentName(afterpay, klarna, venmo, paze, isNewUserFlow, googlePay = { isSelected: false }) {
    let alternatePaymentName = '';

    if (afterpay.isSelected) {
        alternatePaymentName = PAYMENT_TYPES.PAY_AFTERPAY;
    } else if (klarna.isSelected) {
        alternatePaymentName = PAYMENT_TYPES.PAY_KLARNA;
    } else if (venmo.isSelected || (isNewUserFlow && Venmo.isVenmoExpressFlow())) {
        alternatePaymentName = PAYMENT_TYPES.PAY_VENMO;
    } else if (paze.isSelected) {
        alternatePaymentName = PAYMENT_TYPES.PAY_PAZE;
    } else if (googlePay.isSelected) {
        alternatePaymentName = PAYMENT_TYPES.PAY_GOOGLE_PAY;
    }

    return alternatePaymentName;
}

function getInstallment(orderDetails) {
    const amount = orderDetails.priceInfo?.creditCardAmount || orderDetails.priceInfo.paypalAmount;
    const [installmentValue] = skuHelpers.formatInstallmentValue(amount);

    return installmentValue;
}

function checkGiftCardAmountEqualsOrderTotal(priceInfo) {
    return priceInfo?.giftCardAmount && priceInfo?.giftCardAmount === priceInfo?.orderTotal;
}

/**
 * Checks if subscription order (SDU or AutoReplenish)
 * @param {Object} orderDetails - Order details object
 * @returns {boolean} - True if subscription order
 */
function isSubscriptionOrder(orderDetails) {
    const hasAutoReplenish = orderUtils.hasAutoReplenishItems(orderDetails);
    const hasSDU = orderUtils.hasSDUInBasket(orderDetails);

    return hasAutoReplenish || hasSDU;
}

// The default-payment endpoints have inconsistent casing for PayPal: SET/UNSET
// require the backend enum `PAY_PAL`, but GET responses (and analytics probes)
// return the lowercase `paypal` form. Canonicalize both sides — lowercase with
// an explicit `pay_pal` → `paypal` alias — so equality checks stay correct
// wherever we compare payment identifiers
function normalizePaymentName(value) {
    const lower = String(value || '').toLowerCase();

    return lower === 'pay_pal' ? 'paypal' : lower;
}

function checkIsAltenateMethodDefaultPayment(alternatePaymentName, defaultPayment) {
    return (
        (alternatePaymentName === PAYMENT_TYPES.PAY_AFTERPAY && defaultPayment === 'afterpay') ||
        (alternatePaymentName === PAYMENT_TYPES.PAY_KLARNA && defaultPayment === 'klarna') ||
        (alternatePaymentName === PAYMENT_TYPES.PAY_VENMO && defaultPayment === 'venmo')
    );
}

// Paze + Gift Card split payment is US-only and gated behind the ConfigHub flag gcSplitPaymentPazeUS.
function isPazeSplitPaymentWithGCEnabled() {
    return localeUtils.isUS() && Sephora.configurationSettings?.gcSplitPaymentPazeUS?.isEnabled === true;
}

// Afterpay + Gift Card split payment (US + CA), each locale gated behind its own ConfigHub flag.
function isAfterpaySplitPaymentWithGCEnabled() {
    return (
        (localeUtils.isUS() && Sephora.configurationSettings?.gcSplitPaymentAfterpayUS?.isEnabled === true) ||
        (localeUtils.isCanada() && Sephora.configurationSettings?.gcSplitPaymentAfterpayCA?.isEnabled === true)
    );
}

// Afterpay when purchasing a physical gift card (US + CA), each locale gated behind its own ConfigHub flag.
function isAfterpayGCPurchaseEnabled() {
    return (
        (localeUtils.isUS() && Sephora.configurationSettings?.gcPurchaseWithAfterpayUS?.isEnabled === true) ||
        (localeUtils.isCanada() && Sephora.configurationSettings?.gcPurchaseWithAfterpayCA?.isEnabled === true)
    );
}

function isKlarnaSplitPaymentWithGCEnabled() {
    return (
        (localeUtils.isUS() && Sephora.configurationSettings?.gcSplitPaymentKlarnaUS?.isEnabled === true) ||
        (localeUtils.isCanada() && Sephora.configurationSettings?.gcSplitPaymentKlarnaCA?.isEnabled === true)
    );
}

// Google Pay + Gift Card split payment (US + CA), each locale gated behind its own ConfigHub flag.
function isGooglePaySplitPaymentWithGCEnabled() {
    return (
        (localeUtils.isUS() && Sephora.configurationSettings?.gcSplitPaymentGooglepayUS?.isEnabled === true) ||
        (localeUtils.isCanada() && Sephora.configurationSettings?.gcSplitPaymentGooglepayCA?.isEnabled === true)
    );
}

const GIFT_CARD_COMBINABLE_ALTERNATES = {
    [ALTERNATE_PAYMENT_TYPES.PAYPAL]: () => true,
    [ALTERNATE_PAYMENT_TYPES.VENMO]: () => true,
    [ALTERNATE_PAYMENT_TYPES.PAZE]: isPazeSplitPaymentWithGCEnabled,
    [ALTERNATE_PAYMENT_TYPES.AFTERPAY]: isAfterpaySplitPaymentWithGCEnabled,
    [ALTERNATE_PAYMENT_TYPES.KLARNA]: isKlarnaSplitPaymentWithGCEnabled,
    [ALTERNATE_PAYMENT_TYPES.GOOGLE_PAY]: isGooglePaySplitPaymentWithGCEnabled
};

// alternatePaymentName (PAYMENT_TYPES) => ALTERNATE_PAYMENT_TYPES. No PayPal (there is no PAY_PAYPAL key).
const PAYMENT_NAME_TO_ALTERNATE_TYPE = {
    [PAYMENT_TYPES.PAY_VENMO]: ALTERNATE_PAYMENT_TYPES.VENMO,
    [PAYMENT_TYPES.PAY_KLARNA]: ALTERNATE_PAYMENT_TYPES.KLARNA,
    [PAYMENT_TYPES.PAY_AFTERPAY]: ALTERNATE_PAYMENT_TYPES.AFTERPAY,
    [PAYMENT_TYPES.PAY_PAZE]: ALTERNATE_PAYMENT_TYPES.PAZE,
    [PAYMENT_TYPES.PAY_GOOGLE_PAY]: ALTERNATE_PAYMENT_TYPES.GOOGLE_PAY
};

function isGiftCardCombinableAlternateType(alternateType) {
    return Boolean(GIFT_CARD_COMBINABLE_ALTERNATES[alternateType]?.());
}

function isGiftCardCombinablePayment(alternatePaymentName = '') {
    return !alternatePaymentName || isGiftCardCombinableAlternateType(PAYMENT_NAME_TO_ALTERNATE_TYPE[alternatePaymentName]);
}

function isAnyPaymentOptionSelected(paymentGroups, pendingCreditCard = {}) {
    if (
        paymentGroups?.paymentGroupsEntries?.length === 1 &&
        paymentGroups?.paymentGroupsEntries?.[0].paymentGroupType === orderUtils.PAYMENT_GROUP_TYPE.CREDIT_CARD &&
        paymentGroups?.paymentGroupsEntries?.[0]?.paymentGroup?.message &&
        paymentGroups?.paymentGroupsEntries?.[0]?.paymentGroup?.cardNumber.length === 0 &&
        !pendingCreditCard?.cardNumber
    ) {
        return false;
    }

    return true;
}

export default {
    getAvailablePaymentMethods,
    getGiftCardPaymentGroup,
    checkIfAlternateMethodSelected,
    getAlternatePaymentName,
    getInstallment,
    checkGiftCardAmountEqualsOrderTotal,
    checkIsAltenateMethodDefaultPayment,
    isPazeSplitPaymentWithGCEnabled,
    isAfterpaySplitPaymentWithGCEnabled,
    isAfterpayGCPurchaseEnabled,
    isGiftCardCombinablePayment,
    isGiftCardCombinableAlternateType,
    isAnyPaymentOptionSelected,
    isSubscriptionOrder,
    isKlarnaSplitPaymentWithGCEnabled,
    normalizePaymentName,
    isGooglePaySplitPaymentWithGCEnabled
};
